import type { Alert, AlertDetail, AlertNotification, AttemptCounts } from "@/types";
import { countryName } from "@/utils/countryName.ts";
import { formatAsn } from "@/utils/formatAsn.ts";
import { formatDateTime } from "@/utils/formatDateTime.ts";

// Every Alerts sentence lives here, so the wording test covers all of it (spec 3.4 and 3.6).

export const ALERTS_SUBTITLE =
  "Accounts Shield has seen targeted, or signing in unusually after being targeted. " +
  "Each alert is something to check, not a verdict.";
export const NO_ALERTS =
  "No alerts. Shield raises an alert when an account is targeted from many addresses, " +
  "or signs in unusually after being targeted.";
export const NO_MATCHING_ALERTS = "No alerts match these filters.";
export const NOT_SENT = "Not sent";
export const ALERT_GONE = "This alert no longer exists.";
export const ACCOUNT_HISTORY_GONE =
  "The account's full history is kept for 30 days and is no longer available for this alert.";
export const NO_SIGN_INS = "No sign-ins recorded in the last 30 days.";
export const NO_OTHER_SOURCES = "No other sources recorded in the last 30 days.";
export const NO_EVENTS = "No events recorded in the last 30 days.";
export const LOAD_ALERTS_FAILED = "Failed to load alerts.";
export const LOAD_ALERT_FAILED = "Failed to load the alert.";

export const RULE_FILTER_LABELS: Readonly<Record<string, string>> = {
  targeted_account: "Account targeted",
  password_known: "Stopped at the second factor",
  success_after_targeting: "Sign-in after targeting",
  mfa_fatigue: "Sign-in after second-factor failures",
};

export const NOTIFICATION_RULE_LABELS: Readonly<Record<string, string>> = {
  ...RULE_FILTER_LABELS,
  exploited_vulnerability: "New exploited vulnerability in your product",
};

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

function listCountries(codes: readonly string[]): string {
  const names = codes.map(countryName);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** One line per alert, used in the log and as the drawer heading. */
export function alertSummary(alert: Alert): string {
  const sources = count(alert.source_count, "address", "addresses");
  switch (alert.rule) {
    case "targeted_account":
      return alert.countries.length > 0
        ? `Targeted from ${sources} in ${count(alert.countries.length, "country", "countries")}`
        : `Targeted from ${sources}`;
    case "password_known":
      return `Correct password stopped at the second factor, from ${sources}`;
    case "success_after_targeting":
      return alert.success_country
        ? `Signed in from ${countryName(alert.success_country)} after being targeted from ${sources}`
        : `Signed in after being targeted from ${sources}`;
    case "mfa_fatigue":
      return "Signed in after repeated second-factor failures from the same address";
    default:
      return `Alert (${alert.rule})`;
  }
}

function whyTargeted(alert: AlertDetail): string {
  const where = alert.countries.length > 0 ? ` in ${count(alert.countries.length, "country", "countries")}` : "";
  const tried =
    `${count(alert.source_count, "address", "addresses")}${where} tried this account between ` +
    `${formatDateTime(alert.first_seen)} and ${formatDateTime(alert.last_seen)}.`;
  const { minSources, windowHours } = alert.thresholds;
  if (minSources === undefined || windowHours === undefined) return tried;
  return `${tried} The alert threshold is ${count(minSources, "address", "addresses")} within ${count(windowHours, "hour", "hours")}.`;
}

function whyPasswordKnown(alert: AlertDetail): string {
  const stopped =
    alert.evidence.sources.filter((s) => s.attempts.failed_second_factor > 0).length || alert.source_count;
  return (
    `The correct password was entered from ${count(stopped, "address", "addresses")} with no successful ` +
    "sign-in here in the last 30 days, and the second factor stopped it."
  );
}

function baselineBasis(baselineKind: string | null): string {
  if (baselineKind === "account") return " (from this account's history)";
  if (baselineKind === "tenant") return " (company-wide, too little history for this account)";
  return "";
}

function whySuccessAfterTargeting(alert: AlertDetail): string {
  const { evidence } = alert;
  const lookback = alert.thresholds.lookbackDays;
  const before = lookback === undefined ? " before" : ` in the ${count(lookback, "day", "days")} before`;
  const parts = [`${count(alert.source_count, "address", "addresses")} tried this account${before}.`];
  const success = evidence.success;
  if (success) {
    if (evidence.sources.some((s) => s.ip === success.ip)) {
      parts.push(`Then it signed in from ${success.ip}, one of the addresses that had been trying it.`);
    } else {
      const place = success.country ? countryName(success.country) : "an unknown country";
      const network = formatAsn(success.asn, success.as_name);
      parts.push(`Then it signed in from ${place}${network ? ` via ${network}` : ""} at ${formatDateTime(success.at)}.`);
    }
  }
  if (evidence.baseline_countries.length > 0) {
    const verb = evidence.baseline_countries.length === 1 ? "country is" : "countries are";
    const basis = baselineBasis(evidence.baseline_kind);
    parts.push(`Its usual ${verb} ${listCountries(evidence.baseline_countries)}${basis}.`);
  }
  return parts.join(" ");
}

function whyMfaFatigue(alert: AlertDetail): string {
  const { evidence } = alert;
  const successIp = evidence.success?.ip;
  const matchingSource = evidence.sources.find((s) => s.ip === successIp);
  const failures = matchingSource
    ? matchingSource.attempts.failed_second_factor
    : evidence.sources.reduce((sum, s) => sum + s.attempts.failed_second_factor, 0);
  const ip = successIp ?? evidence.sources[0]?.ip;
  const minutes = alert.thresholds.windowMinutes;
  const within = minutes === undefined ? "" : ` within ${count(minutes, "minute", "minutes")}`;
  const failedText =
    failures === 0
      ? "Failed second-factor attempts"
      : count(failures, "failed second-factor attempt", "failed second-factor attempts");
  return (
    `${failedText}${ip ? ` from ${ip}` : ""}, ` + `then a successful sign-in from the same address${within}.`
  );
}

/** Why the alert fired, from the thresholds and the evidence frozen with it. */
export function whyItFired(alert: AlertDetail): string {
  switch (alert.rule) {
    case "targeted_account":
      return whyTargeted(alert);
    case "password_known":
      return whyPasswordKnown(alert);
    case "success_after_targeting":
      return whySuccessAfterTargeting(alert);
    case "mfa_fatigue":
      return whyMfaFatigue(alert);
    default:
      return `Rule: ${alert.rule}`;
  }
}

export function whatToCheck(rule: string): string | null {
  switch (rule) {
    case "targeted_account":
      return "The username is probably on an attacker's list. Consider telling the user.";
    case "password_known":
      return "Someone else has the password. Have it changed.";
    case "success_after_targeting":
    case "mfa_fatigue":
      return "Confirm with the user that this sign-in was theirs.";
    default:
      return null;
  }
}

const EVENT_LABELS: Readonly<Record<string, string>> = {
  success: "Signed in",
  invalid_password: "Wrong password",
  failed_second_factor: "Stopped at the second factor",
  blocked: "Blocked before the sign-in",
};

export function eventLabel(outcome: string): string {
  return EVENT_LABELS[outcome] ?? outcome;
}

export function attemptsText(attempts: AttemptCounts): string {
  const parts: string[] = [];
  if (attempts.invalid_password > 0) parts.push(`${attempts.invalid_password.toLocaleString("en-US")} wrong password`);
  if (attempts.failed_second_factor > 0) {
    parts.push(`${attempts.failed_second_factor.toLocaleString("en-US")} stopped at the second factor`);
  }
  if (attempts.blocked > 0) parts.push(`${attempts.blocked.toLocaleString("en-US")} blocked`);
  return parts.length > 0 ? parts.join(", ") : "—";
}

const CHANNEL_LABELS: Readonly<Record<string, string>> = { email: "Email", teams: "Teams" };

export function channelLabel(channel: string): string {
  return CHANNEL_LABELS[channel] ?? channel;
}

/** An email address as is; anything else with its channel, e.g. "IT-larm (Teams)". */
export function recipientLabel(notification: AlertNotification): string {
  return notification.channel === "email"
    ? notification.recipient
    : `${notification.recipient} (${channelLabel(notification.channel)})`;
}

export function shortHash(hash: string): string {
  return `${hash.slice(0, 8)}…`;
}

// Notification groups: the Mideye admin panel on the Alerts page.
export const NO_GROUPS = "No notification groups yet.";
export const RECIPIENTS_HINT = "One address per line.";
export const DELETE_GROUP_CONFIRM = "Delete this group?";
export const LOAD_GROUPS_FAILED = "Could not load the notification groups.";
export const SETTINGS_CONFLICT = "Someone else changed these settings. Reload to see their changes.";
export const SETTINGS_UNAVAILABLE = "Notification settings aren't available here yet.";
export const SAVE_FAILED = "Could not save. Try again.";

export function notificationGroupsTitle(company: string): string {
  return `Notification groups — ${company}`;
}

export function recipientCount(n: number): string {
  return count(n, "recipient", "recipients");
}

export function lastChanged(by: string, at: number): string {
  return `Last changed by ${by} at ${formatDateTime(at)}`;
}

export function unlikelyAddress(entry: string): string {
  return `"${entry}" doesn't look like an email address.`;
}
